import { useState, useEffect, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { QuizBuilderData } from '@/components/quiz-builder/QuizBuilder';

interface QuizDraft {
  id: string;
  name: string;
  original_prompt: string;
  is_daily_knowledge: boolean;
  questions: any[];
  lastSaved: string;
  isEditMode: boolean;
  existingQuizletId?: string;
}

const DRAFT_KEY = 'knowblemind-quiz-drafts';
const MAX_DRAFTS = 10;
const AUTO_SAVE_DELAY = 2000; // 2 seconds

export function useDraftManager(
  quizData: QuizBuilderData,
  isEditMode: boolean,
  existingQuizletId?: string
) {
  const [draftId, setDraftId] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState<Date | null>(null);

  // Generate or retrieve draft ID
  useEffect(() => {
    if (!draftId) {
      if (isEditMode && existingQuizletId) {
        // For edit mode, use quizlet ID as draft ID
        setDraftId(`edit-${existingQuizletId}`);
      } else {
        // For new quiz, generate new draft ID
        setDraftId(uuidv4());
      }
    }
  }, [draftId, isEditMode, existingQuizletId]);

  // Check for existing draft on mount
  useEffect(() => {
    if (!draftId) return;

    const drafts = loadAllDrafts();
    const existingDraft = drafts.find((d) => d.id === draftId);

    if (existingDraft) {
      setHasDraft(true);
    }
  }, [draftId]);

  // Load all drafts from localStorage
  const loadAllDrafts = useCallback((): QuizDraft[] => {
    try {
      const stored = localStorage.getItem(DRAFT_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error loading drafts:', error);
      return [];
    }
  }, []);

  // Save draft to localStorage
  const saveDraft = useCallback(
    (data: QuizBuilderData) => {
      if (!draftId) return;

      try {
        const draft: QuizDraft = {
          id: draftId,
          name: data.name,
          original_prompt: data.original_prompt,
          is_daily_knowledge: data.is_daily_knowledge,
          questions: data.questions,
          lastSaved: new Date().toISOString(),
          isEditMode,
          existingQuizletId,
        };

        // Load existing drafts
        const drafts = loadAllDrafts();

        // Remove old draft with same ID if exists
        const filteredDrafts = drafts.filter((d) => d.id !== draftId);

        // Add new draft at the beginning
        const updatedDrafts = [draft, ...filteredDrafts];

        // Keep only last MAX_DRAFTS
        const trimmedDrafts = updatedDrafts.slice(0, MAX_DRAFTS);

        // Save to localStorage
        localStorage.setItem(DRAFT_KEY, JSON.stringify(trimmedDrafts));

        setLastAutoSaved(new Date());
      } catch (error) {
        console.error('Error saving draft:', error);
      }
    },
    [draftId, isEditMode, existingQuizletId, loadAllDrafts]
  );

  // Debounced auto-save
  const debouncedSave = useMemo(() => {
    let timeoutId: NodeJS.Timeout;

    return (data: QuizBuilderData) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Only save if there's meaningful content
        if (data.name.trim() !== '' || data.questions.length > 0) {
          saveDraft(data);
        }
      }, AUTO_SAVE_DELAY);
    };
  }, [saveDraft]);

  // Auto-save whenever quiz data changes
  useEffect(() => {
    debouncedSave(quizData);
  }, [quizData, debouncedSave]);

  // Load draft by ID
  const loadDraft = useCallback((): QuizBuilderData | null => {
    if (!draftId) return null;

    const drafts = loadAllDrafts();
    const draft = drafts.find((d) => d.id === draftId);

    if (!draft) return null;

    return {
      name: draft.name,
      original_prompt: draft.original_prompt,
      is_daily_knowledge: draft.is_daily_knowledge,
      questions: draft.questions,
    };
  }, [draftId, loadAllDrafts]);

  // Clear draft
  const clearDraft = useCallback(() => {
    if (!draftId) return;

    try {
      const drafts = loadAllDrafts();
      const filteredDrafts = drafts.filter((d) => d.id !== draftId);
      localStorage.setItem(DRAFT_KEY, JSON.stringify(filteredDrafts));
      setHasDraft(false);
      setLastAutoSaved(null);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  }, [draftId, loadAllDrafts]);

  // Get draft metadata (for showing in modal)
  const getDraftMetadata = useCallback((): {
    lastSaved: Date | null;
    questionCount: number;
  } | null => {
    if (!draftId) return null;

    const drafts = loadAllDrafts();
    const draft = drafts.find((d) => d.id === draftId);

    if (!draft) return null;

    return {
      lastSaved: new Date(draft.lastSaved),
      questionCount: draft.questions.length,
    };
  }, [draftId, loadAllDrafts]);

  // Manual save (for user-triggered saves)
  const manualSave = useCallback(() => {
    saveDraft(quizData);
  }, [quizData, saveDraft]);

  return {
    hasDraft,
    loadDraft,
    clearDraft,
    getDraftMetadata,
    manualSave,
    lastAutoSaved,
  };
}
